import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HeroSection from './HeroSection';
import ReferencesSlider from './ReferencesSlider';
import ServicesSection from './ServicesSection';
import CategoriesSection from './CategoriesSection';
import ConsultationForm from './ConsultationForm';
import SearchSystem from './SearchSystem';
import ProjectShareForm from './ProjectShareForm';
import FAQSection from './FAQSection';
import BlogSystem from './BlogSystem';
import GogiAISystem from './GogiAISystem';
import MediaDisplay from './MediaDisplay';
import { normalizeUrl } from '../utils/format';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Zap,
  Users,
  TrendingUp,
  Shield,
  Award,
  Clock,
  CheckCircle,
  Star,
  ArrowRight,
  MessageSquare,
  Lightbulb,
  Target,
  Globe,
  Rocket,
  Bot,
  GraduationCap,
  Video,
  MessageCircle,
  Phone,
  LayoutGrid,
  ShoppingBag,
  Building2,
  MousePointer2,
  Search,
  Eye
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { API_URLS } from '../config/api';
import { formatPrice as formatPriceUtil } from '../utils/format';

const TYPE_CONFIG = {
  ecommerce: {
    label: 'E-ticaret Tasarımları',
    icon: ShoppingBag,
  },
  corporate: {
    label: 'Kurumsal Site Tasarımları',
    icon: Building2,
  },
  easy: {
    label: 'Kolay Site Tasarımları',
    icon: MousePointer2,
  },
};

function normalizeImageUrl(url) {
  const BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/admin_api.php')) {
    const m = url.match(/getScreenshot&file=([^&]+)$/);
    if (m && m[1]) return `${BASE_URL}/backend/uploads/${m[1]}`;
    return `${BASE_URL}/backend${url}`;
  }
  return `${BASE_URL}${url}`;
}

function getImageTransitionClass(naturalHeight) {
  if (!naturalHeight) return '';
  if (naturalHeight < 800) return 'image-short';
  if (naturalHeight < 1500) return 'image-medium';
  if (naturalHeight < 2500) return 'image-long';
  return 'image-very-long';
}

function getLimitedCategories(item, max = 2) {
  const names = [];
  if (item.category_names && Array.isArray(item.category_names) && item.category_names.length > 0) {
    names.push(...item.category_names);
  }
  if (names.length === 0 && item.category) {
    names.push(item.category);
  }
  return names.slice(0, max);
}

function getLimitedTags(item, max = 3) {
  if (!item.tags) return [];
  let tags = [];
  if (typeof item.tags === 'string') {
    try {
      tags = JSON.parse(item.tags);
    } catch {
      tags = item.tags.split(',').map(t => t.trim());
    }
  } else if (Array.isArray(item.tags)) {
    tags = item.tags;
  }
  return tags.slice(0, max);
}

// Basit, Türkçe uyumlu normalizasyon (elastik arama için)
function normalizeText(value) {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/İ/g, 'i')
    .replace(/Ğ/g, 'g')
    .replace(/Ü/g, 'u')
    .replace(/Ş/g, 's')
    .replace(/Ö/g, 'o')
    .replace(/Ç/g, 'c');
}

function TemplateCard({ template }) {
  const navigate = useNavigate();
  const { type } = template;
  const typeConfig = TYPE_CONFIG[type] || TYPE_CONFIG.ecommerce;
  const TypeIcon = typeConfig.icon || LayoutGrid;

  const getDetailUrl = () => {
    // Tasarım tipine göre detay sayfası URL'ini döndür
    // Her zaman slug kullan (slug yoksa veya tpl_ ile başlıyorsa id kullan)
    if (!template.id && !template.slug) return null;
    
    // Slug varsa ve tpl_ ile başlamıyorsa slug kullan, yoksa id kullan
    const identifier = (template.slug && !template.slug.startsWith('tpl_')) 
      ? template.slug 
      : (template.id || template.slug);
    
    if (!identifier) return null;
    
    if (type === 'ecommerce') {
      return `/eticaret-tasarimlari/${encodeURIComponent(identifier)}`;
    }
    
    if (type === 'corporate') {
      return `/kurumsal-web-sitesi-tasarimlari/${encodeURIComponent(identifier)}`;
    }
    
    // Kolay site tasarımları (easy) için: slug kullan (slug yoksa veya tpl_ ile başlıyorsa id kullan)
    return `/hazir-web-sitesi-tasarimlari/${encodeURIComponent(identifier)}`;
  };

  const handlePrimaryAction = () => {
    // Detay sayfası olanlar için route'a yönlendir, diğerleri için demo bağlantısı aç
    const detailUrl = getDetailUrl();
    if (detailUrl) {
      navigate(detailUrl);
      return;
    }
    if (template.demo_url) {
      const normalizedUrl = normalizeUrl(template.demo_url);
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const categories = getLimitedCategories(template);
  const tags = getLimitedTags(template);
  const hasDemo = !!template.demo_url;
  const hasAdmin = !!template.admin_url;
  const hasPurchase = !!template.purchase_url;

  const priceValue =
    template.price !== undefined && template.price !== null
      ? template.price
      : template.sale_price !== undefined && template.sale_price !== null
      ? template.sale_price
      : null;

  const formattedPrice =
    priceValue !== null && priceValue !== ''
      ? formatPriceUtil(priceValue, '₺', 0, 0)
      : null;

  return (
    <Card className="group overflow-hidden border border-gray-200 hover:border-[#7A5AFF]/50 hover:shadow-xl transition-all duration-300 bg-white flex flex-col h-full">
      <div className="relative aspect-[16/10] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        {template.image_url || template.image ? (
          <img
            src={normalizeImageUrl(template.image_url || template.image)}
            alt={template.name}
            loading="lazy"
            className="w-full h-full object-cover object-top transition-all duration-[20s] ease group-hover:object-bottom"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            onLoad={(e) => {
              const nh = e.target.naturalHeight;
              const cls = getImageTransitionClass(nh);
              if (cls) e.target.classList.add(cls);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
            Önizleme görseli henüz eklenmedi
          </div>
        )}

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <Badge className="bg-white/90 text-gray-800 border border-gray-200 text-[10px] font-semibold uppercase tracking-wide">
            <TypeIcon className="w-3 h-3 mr-1" />
            {typeConfig.label}
          </Badge>
        </div>
      </div>

      <CardContent className="flex-1 flex flex-col p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2">
            {template.name}
          </h3>
        </div>

        {template.description && (
          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-3">
            {template.description}
          </p>
        )}

        {(categories.length > 0 || tags.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {categories.map((cat, idx) => (
              <Badge
                key={`cat-${idx}`}
                variant="outline"
                className="border-[#7A5AFF]/30 bg-[#7A5AFF]/10 text-[10px] sm:text-xs text-[#7A5AFF]"
              >
                {cat}
              </Badge>
            ))}
            {tags.map((tag, idx) => (
              <Badge
                key={`tag-${idx}`}
                variant="outline"
                className="border-gray-200 bg-gray-50 text-[10px] sm:text-xs text-gray-700"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}

        <div className="mt-auto pt-2 space-y-3">
          {/* Tasarımı İncele Button - Always visible if slug or id exists */}
          {getDetailUrl() && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full font-semibold transition-all duration-300 transform hover:scale-105 bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              onClick={() => navigate(getDetailUrl())}
            >
              <Eye className="w-4 h-4 mr-2" />
              <span>Tasarımı İncele</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          )}

          {/* Action buttons - Admin and Purchase */}
          {(hasAdmin || hasPurchase) && (
            <div className="flex flex-wrap gap-2 justify-between">
              {hasAdmin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs sm:text-sm bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
                  onClick={() => window.open(template.admin_url, '_blank', 'noopener,noreferrer')}
                >
                  Admin
                </Button>
              )}
              {hasPurchase && (
                <Button
                  type="button"
                  size="sm"
                  className="flex-1 text-xs sm:text-sm bg-[#7A5AFF] hover:bg-[#8b6bff] text-white"
                  onClick={() => window.open(template.purchase_url, '_blank', 'noopener,noreferrer')}
                >
                  Satın Al
                </Button>
              )}
            </div>
          )}

          {/* Demo button - separate if exists */}
          {hasDemo && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full text-xs sm:text-sm bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              onClick={() => {
                const normalizedUrl = normalizeUrl(template.demo_url);
                window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              Demo
            </Button>
          )}

          {/* Price bottom centered */}
          {formattedPrice && (
            <div className="pt-1 text-center">
              <div className="text-sm sm:text-base font-semibold text-gray-900">
                {formattedPrice}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const EnhancedHomePage = () => {
  const navigate = useNavigate();
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showProjectShareForm, setShowProjectShareForm] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [designTemplates, setDesignTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

  // Sayfa yüklendiğinde fade-in animasyonu başlat (CorporateWebsitePage gibi)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Fetch page content from database
  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const res = await fetch(`${apiBase}/backend/admin_api.php?action=getPageContent&slug=anasayfa`);
        const data = await res.json();
        
        if (data.status === 'success' && data.data) {
          setPageContent(data.data);
        }
      } catch (error) {
        // Silently handle error
      } finally {
        setLoading(false);
      }
    };

    const fetchSettings = async () => {
      try {
        const res = await fetch(`${apiBase}/backend/admin_api.php?action=getCompanySettings`);
        if (res.ok) {
          const json = await res.json();
          if (json.status === 'success' && json.data?.whatsapp) {
            setWhatsappNumber(json.data.whatsapp);
          }
        }
      } catch (error) {
        // Silently handle error
      }
    };

    fetchPageContent();
    fetchSettings();
  }, [apiBase]);

  // Fetch design templates for homepage
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const params = 'limit=60&offset=0';
        const [ecomRes, corpRes, easyRes] = await Promise.all([
          fetch(`${API_URLS.ECOMMERCE_DESIGNS}&${params}`),
          fetch(`${API_URLS.CORPORATE_DESIGNS}&${params}`),
          fetch(API_URLS.ADMIN.WEBSITE_TEMPLATES),
        ]);

        const [ecomJson, corpJson, easyJson] = await Promise.all([
          ecomRes.json(),
          corpRes.json(),
          easyRes.json(),
        ]);

        const ecomList = Array.isArray(ecomJson?.data) ? ecomJson.data : [];
        const corpList = Array.isArray(corpJson?.data) ? corpJson.data : [];
        const easyList = Array.isArray(easyJson) ? easyJson : (easyJson?.data || []);

        // Tüm tasarımları birleştir (arama için)
        const allNormalized = [
          ...ecomList.map((item) => ({ ...item, type: 'ecommerce' })),
          ...corpList.map((item) => ({ ...item, type: 'corporate' })),
          ...easyList.map((item) => ({ ...item, type: 'easy' })),
        ];

        setAllTemplates(allNormalized);

        // Varsayılan olarak ilk 8 tasarımı göster
        const defaultDisplay = [
          ...ecomList.slice(0, 3).map((item) => ({ ...item, type: 'ecommerce' })),
          ...corpList.slice(0, 3).map((item) => ({ ...item, type: 'corporate' })),
          ...easyList.slice(0, 2).map((item) => ({ ...item, type: 'easy' })),
        ];

        setDesignTemplates(defaultDisplay);
      } catch (e) {
        console.error('HomePage templates fetch error:', e);
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Arama yapıldığında tasarımları filtrele
  useEffect(() => {
    if (!searchTerm.trim()) {
      // Arama yoksa varsayılan 8 tasarımı göster
      const defaultDisplay = [
        ...allTemplates.filter(t => t.type === 'ecommerce').slice(0, 3),
        ...allTemplates.filter(t => t.type === 'corporate').slice(0, 3),
        ...allTemplates.filter(t => t.type === 'easy').slice(0, 2),
      ];
      setDesignTemplates(defaultDisplay);
      return;
    }

    const termNorm = normalizeText(searchTerm.trim());
    const filtered = allTemplates.filter((tpl) => {
      const baseTexts = [
        tpl.name,
        tpl.description,
        tpl.short_description,
        tpl.long_description,
      ].filter(Boolean);

      const categoryTexts = [];
      if (Array.isArray(tpl.category_names)) {
        categoryTexts.push(...tpl.category_names);
      }
      if (tpl.category) {
        categoryTexts.push(tpl.category);
      }

      const tagTexts = [];
      if (tpl.tags) {
        let tags = [];
        if (typeof tpl.tags === 'string') {
          try {
            tags = JSON.parse(tpl.tags);
          } catch {
            tags = tpl.tags.split(',').map((t) => t.trim());
          }
        } else if (Array.isArray(tpl.tags)) {
          tags = tpl.tags;
        }
        tagTexts.push(...tags);
      }

      const haystack = normalizeText(
        [...baseTexts, ...categoryTexts, ...tagTexts].join(' ')
      );

      // Elastik arama: içerme + kelime başı eşleşmesi
      if (haystack.includes(termNorm)) return true;
      const words = haystack.split(/\s+/);
      return words.some((w) => w.startsWith(termNorm));
    });

    setDesignTemplates(filtered);
  }, [searchTerm, allTemplates]);

  // Arama dropdown'ı için öneriler (ilk 10 sonuç)
  const searchSuggestions = useMemo(() => {
    const term = searchTerm.trim();
    if (term.length < 2) return [];

    const termNorm = normalizeText(term);
    const matches = allTemplates.filter((tpl) => {
      const baseTexts = [tpl.name, tpl.description].filter(Boolean);
      const categoryTexts = [];
      if (Array.isArray(tpl.category_names)) {
        categoryTexts.push(...tpl.category_names);
      }
      if (tpl.category) {
        categoryTexts.push(tpl.category);
      }
      const tagTexts = [];
      if (tpl.tags) {
        let tags = [];
        if (typeof tpl.tags === 'string') {
          try {
            tags = JSON.parse(tpl.tags);
          } catch {
            tags = tpl.tags.split(',').map((t) => t.trim());
          }
        } else if (Array.isArray(tpl.tags)) {
          tags = tpl.tags;
        }
        tagTexts.push(...tags);
      }
      const haystack = normalizeText(
        [...baseTexts, ...categoryTexts, ...tagTexts].join(' ')
      );
      if (haystack.includes(termNorm)) return true;
      const words = haystack.split(/\s+/);
      return words.some((w) => w.startsWith(termNorm));
    });

    return matches.slice(0, 10);
  }, [allTemplates, searchTerm]);


  // Force scroll to top and clear hash on component mount
  useEffect(() => {
    // Clear any hash immediately
    if (window.location.hash) {
      window.location.hash = '';
    }
    
    // Force scroll to top
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Additional check after render
    setTimeout(() => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }, 0);
  }, []);

  // Global event to open Project Share from other sections
  useEffect(() => {
    const handler = () => setShowProjectShareForm(true);
    window.addEventListener('openProjectShareForm', handler);
    return () => window.removeEventListener('openProjectShareForm', handler);
  }, []);

  // Global open for consultation form
  useEffect(() => {
    const handler = () => setShowConsultationForm(true);
    window.addEventListener('openConsultationForm', handler);
    return () => window.removeEventListener('openConsultationForm', handler);
  }, []);

  // Icon mapping for dynamic data
  const iconMap = {
    Zap, Shield, Award, Clock, Users, CheckCircle, Star, Bot,
    TrendingUp, GraduationCap, Video, Rocket, Lightbulb, Target
  };

  // Get features, testimonials, and success stories from pageContent or use defaults
  // Parse features if it's a JSON string
  let features = pageContent?.features_section || [];
  if (typeof features === 'string' && features.trim()) {
    try {
      features = JSON.parse(features);
    } catch (e) {
      features = [];
    }
  }
  if (!Array.isArray(features)) {
    features = [];
  }

  // Parse testimonials if it's a JSON string
  let testimonials = pageContent?.testimonials || [];
  if (typeof testimonials === 'string' && testimonials.trim()) {
    try {
      testimonials = JSON.parse(testimonials);
    } catch (e) {
      testimonials = [];
    }
  }
  if (!Array.isArray(testimonials)) {
    testimonials = [];
  }

  // Parse success stories if it's a JSON string
  let successStories = pageContent?.success_stories || [];
  if (typeof successStories === 'string' && successStories.trim()) {
    try {
      successStories = JSON.parse(successStories);
    } catch (e) {
      successStories = [];
    }
  }
  if (!Array.isArray(successStories)) {
    successStories = [];
  }

  return (
    <div 
      className="min-h-screen"
      style={{ 
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 0.8s ease-in-out'
      }}
    >
      <style>{`
        .group:hover .group-hover\\:object-bottom {
          transition: all 20s ease;
        }
        .image-short { transition: all 3s ease !important; }
        .image-medium { transition: all 8s ease !important; }
        .image-long { transition: all 15s ease !important; }
        .image-very-long { transition: all 25s ease !important; }
        /* Anasayfa hero input placeholder beyaz */
        .home-hero-section input::placeholder {
          color: white !important;
        }
      `}</style>
      {/* Enhanced Hero Section with Search */}
      <section className="home-hero-section relative bg-gradient-to-br from-black via-[#0f0a1f] to-[#1a0a2e] text-white overflow-hidden">
        {/* Animated Background - #000000 + #7A5AFF */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-[#7A5AFF]/20 to-[#7A5AFF]/5"></div>
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#7A5AFF]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#7A5AFF]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative container mx-auto px-6 pt-8 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sol Taraf - İçerik */}
            <div className="text-center md:text-left">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur px-6 py-3 rounded-full mb-8">
                <Rocket className="w-5 h-5" />
                <span className="font-medium">{pageContent?.hero_badge_text || 'Dijital dönüşümde güvenilir çözüm ortağınız'}</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      pageContent?.hero_title ||
                        '<span class="bg-gradient-to-r from-[#7A5AFF] via-[#9d7dff] to-[#7A5AFF] bg-clip-text text-transparent">Dijital Dünya</span><br/><span class="text-white">Uzmanlarınız</span>'
                    )
                  }}
                />
              </h1>

              <p className="text-xl md:text-2xl text-gray-300 mb-12">
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      pageContent?.hero_description ||
                        'E-ticaret, web tasarım, mobil uygulama ve yapay zeka çözümleriyle işinizi büyütün. <span class="text-[#7A5AFF] font-semibold">Website Al</span> ile hedeflerinize daha hızlı ulaşın.'
                    )
                  }}
                />
              </p>

              {/* Enhanced Search Bar */}
              <div className="mb-12">
                <SearchSystem
                  placeholder="Ne arıyorsunuz? Örn: E-ticaret sitesi, logo tasarımı, mobil uygulama..."
                />
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button
                  onClick={() => setShowConsultationForm(true)}
                  size="lg"
                  className="bg-[#7A5AFF] hover:bg-[#8b6bff] text-white font-bold px-8 py-4 text-lg shadow-lg shadow-[#7A5AFF]/30"
                >
                  <Zap className="w-6 h-6 mr-2" />
                  {pageContent?.hero_button1_text || 'Ücretli Danışmanlık Al'}
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg"
                  onClick={() => setShowProjectShareForm(true)}
                >
                  <Target className="w-6 h-6 mr-2" />
                  {pageContent?.hero_button2_text || 'Projeni Paylaş'}
                </Button>
              </div>
            </div>

            {/* Sağ Taraf - Resim/Video */}
            <div className="hidden lg:flex justify-center items-center">
              <div className="relative w-full max-w-lg">
                <div className="absolute inset-0 bg-gradient-to-r from-[#7A5AFF]/20 to-black/20 rounded-full blur-3xl"></div>
                <MediaDisplay
                  src={pageContent?.hero_image_url}
                  fallbackSrc="/website-al-logo.png"
                  alt="Website Al - Dijital Çözümler"
                  className="relative w-full h-auto object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Consultation Form Modal */}
      {showConsultationForm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConsultationForm(false);
            }
          }}
        >
          <div className="relative max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <ConsultationForm
              isModal={true}
              onClose={() => setShowConsultationForm(false)}
            />
          </div>
        </div>
      )}

      {/* Project Share Modal */}
      {showProjectShareForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4">
          <div className="relative z-50 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <Button
              onClick={() => setShowProjectShareForm(false)}
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white w-10 h-10 p-0 rounded-full"
            >
              ✕
            </Button>
            <ProjectShareForm onClose={() => setShowProjectShareForm(false)} />
          </div>
        </div>
      )}

      {/* Categories Section */}
      {/* References Slider */}
      <div style={{ 
        opacity: pageLoaded ? 1 : 0,
        transform: pageLoaded ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s'
      }}>
        <ReferencesSlider />
      </div>

      <div>
        <CategoriesSection sectionContent={pageContent?.additional_sections?.projects} />
      </div>

      {/* Services Section */}
      <div>
        <ServicesSection sectionContent={pageContent?.additional_sections?.services} />
      </div>

      {/* Web Design Templates Section */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-[#f5f3ff] to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-[#7A5AFF]/15 text-[#7A5AFF] px-6 py-3 rounded-full text-sm font-medium mb-6">
              <LayoutGrid className="w-5 h-5" />
              <span>Web Sitesi Tasarımları</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Profesyonel Web Sitesi Tasarımları
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              E-ticaret, kurumsal ve kolay site tasarımlarımızdan ilham alın. Modern, responsive ve SEO uyumlu şablonlar.
            </p>

            {/* Arama Barı */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="relative">
                <Search className="w-4 h-4 sm:w-5 sm:h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Tasarım Ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="design-area-input w-full pl-9 pr-3 py-2 sm:py-2.5 rounded-full border border-gray-300 bg-white text-sm sm:text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-[#7A5AFF] focus:border-[#7A5AFF] text-gray-900 placeholder:text-gray-400"
                />
                {/* Arama öneri dropdown'ı */}
                {searchSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto z-30 text-left">
                    {searchSuggestions.map((tpl) => {
                      const cfg = TYPE_CONFIG[tpl.type] || TYPE_CONFIG.ecommerce;
                      const Icon = cfg.icon || LayoutGrid;
                      return (
                        <button
                          key={`${tpl.type}-${tpl.id || tpl.slug || tpl.name}`}
                          type="button"
                          onClick={() => {
                            setSearchTerm(tpl.name || '');
                          }}
                          className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-[#7A5AFF]/10 text-sm"
                        >
                          <span className="flex-1 text-gray-800 text-left line-clamp-1">
                            {tpl.name}
                          </span>
                          <span className="flex items-center gap-1 text-[10px] text-gray-500">
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {templatesLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AFF]"></div>
            </div>
          ) : designTemplates.length > 0 ? (
            <>
              {searchTerm.trim() && (
                <div className="text-center mb-6">
                  <p className="text-gray-600">
                    <span className="font-semibold text-[#7A5AFF]">{designTemplates.length}</span> tasarım bulundu
                    {searchTerm.trim() && (
                      <span className="ml-2">
                        "<span className="font-semibold">{searchTerm}</span>" için
                      </span>
                    )}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {designTemplates.map((template, index) => (
                  <div
                    key={`${template.type}-${template.id || index}`}
                    className={!searchTerm.trim() && index >= 3 ? 'hidden sm:block' : ''}
                  >
                    <TemplateCard template={template} />
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Button
                  size="lg"
                  onClick={() => navigate('/web-sitesi-tasarimlari')}
                  className="bg-[#7A5AFF] hover:bg-[#8b6bff] text-white transform hover:scale-105 transition-all duration-300 text-lg px-10 py-6 shadow-lg shadow-[#7A5AFF]/30"
                >
                  <LayoutGrid className="w-5 h-5 mr-2" />
                  Tüm Tasarımları Gör
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </>
          ) : searchTerm.trim() ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg mb-4">
                "<span className="font-semibold">{searchTerm}</span>" için sonuç bulunamadı
              </p>
              <Button
                variant="outline"
                onClick={() => setSearchTerm('')}
                className="text-[#7A5AFF] border-[#7A5AFF] hover:bg-[#7A5AFF]/10"
              >
                Aramayı Temizle
              </Button>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Tasarımlar yükleniyor...
            </div>
          )}
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-black to-[#1a0a2e] text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {pageContent?.success_stories_title || 'Başarı Hikayeleri'}
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              {pageContent?.success_stories_description || 'Müşterilerimizin Website Al ile nasıl başarıya ulaştığını görün'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {successStories.map((story, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur border-white/20 text-white">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <Badge className="bg-green-500 text-white px-4 py-2">
                      {story.result}
                    </Badge>
                  </div>

                  <h3 className="text-2xl font-bold mb-2">{story.title}</h3>
                  <p className="text-white/70 mb-1">{story.company}</p>
                  <p className="text-white mb-4">{story.description}</p>

                  <Badge variant="outline" className="border-[#7A5AFF]/50 text-white/80">
                    {story.service}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  <p className="text-white mb-4 italic">"{testimonial.text}"</p>

                  <div className="border-t border-white/20 pt-4">
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-white/70 text-sm">{testimonial.company}</p>
                    <Badge className="mt-2 bg-[#7A5AFF] text-white text-xs">
                      {testimonial.service}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gogi AI Section - Kompakt Yatay Layout */}
      <section className="py-10 md:py-16 bg-gradient-to-br from-[#f5f3ff] to-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto">
            {/* Sol Taraf - İçerik */}
            <div className="space-y-4 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 bg-[#7A5AFF]/15 text-[#7A5AFF] px-6 py-3 rounded-full text-sm font-medium transform hover:scale-105 transition-transform duration-300">
                <Bot className="w-5 h-5" />
                <span>{pageContent?.additional_sections?.gogi_ai?.badge_text || 'Yapay Zeka'}</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      pageContent?.additional_sections?.gogi_ai?.title || '<span class="text-[#7A5AFF]">Gogi AI</span> ile Tanışın'
                    )
                  }}
                />
              </h2>

              <p className="text-lg text-gray-600 leading-relaxed">
                {pageContent?.additional_sections?.gogi_ai?.description || 'Yapay zeka destekli akıllı chatbot asistanınız. Bütün siteyi tarayarak site içeriğini öğrenir, sorularınıza anında yanıt verir.'}
              </p>

              {/* Kompakt Özellikler - veritabanından gogi_ai.stats veya varsayılan */}
              {(() => {
                const iconMap = { Bot, Zap, TrendingUp, Shield };
                const defaultStats = [
                  { icon: 'Bot', label: '7/24 AI', color: 'text-[#7A5AFF]' },
                  { icon: 'Zap', label: 'Anında', color: 'text-[#5a3fd9]' },
                  { icon: 'TrendingUp', label: 'Akıllı', color: 'text-green-600' },
                  { icon: 'Shield', label: 'Güvenli', color: 'text-orange-600' }
                ];
                const stats = (pageContent?.additional_sections?.gogi_ai?.stats && pageContent.additional_sections.gogi_ai.stats.length > 0)
                  ? pageContent.additional_sections.gogi_ai.stats
                  : defaultStats;
                return (
              <div className="grid grid-cols-4 lg:grid-cols-2 gap-3 pt-2">
                {stats.map((stat, index) => {
                  const IconComponent = iconMap[stat.icon] || iconMap.Bot;
                  const color = stat.color || 'text-[#7A5AFF]';
                  const label = stat.label || stat.text || '';
                  return (
                  <div key={index} className="flex items-center space-x-2 justify-center lg:justify-start">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${
                      color === 'text-[#7A5AFF]' || !color ? 'from-[#7A5AFF]/20 to-[#7A5AFF]/30' : 
                      color === 'text-[#5a3fd9]' ? 'from-[#7A5AFF]/15 to-[#7A5AFF]/25' : 
                      color === 'text-green-600' ? 'from-green-100 to-green-200' : 
                      'from-orange-100 to-orange-200'
                    } flex items-center justify-center`}>
                      <IconComponent className={`w-4 h-4 ${color}`} />
                    </div>
                    <span className="text-gray-700 font-medium text-sm">{label}</span>
                  </div>
                  );
                })}
              </div>
                );
              })()}

              {/* AI Chatbot Button */}
              <div className="pt-4 flex justify-center lg:justify-start">
                <a href={pageContent?.additional_sections?.gogi_ai?.button_link || '/yapay-zeka'}>
                  <Button size="lg" className="bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6] text-white transform hover:scale-105 transition-all duration-300 px-8 py-6 shadow-lg shadow-[#7A5AFF]/30">
                    <Bot className="w-5 h-5 mr-2" />
                    {pageContent?.additional_sections?.gogi_ai?.button_text || 'AI Chatbot'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Sağ Taraf - GogiAI Görsel */}
            <div className="flex justify-center">
              <GogiAISystem imageUrl={pageContent?.additional_sections?.gogi_ai?.image_url} />
            </div>
          </div>
        </div>
      </section>

      {/* Education Section - Asimetrik Layout */}
      <section className="py-10 md:py-20 bg-gradient-to-br from-[#7A5AFF]/5 via-white to-black/5">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center max-w-7xl mx-auto">
            {/* Sol - Video Kartları (5 kolon) */}
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {(pageContent?.additional_sections?.education?.video_cards || []).map((feature, index) => {
                  const IconComponent = iconMap[feature.icon] || Video;
                  return (
                    <a
                      key={index}
                      href="/egitim"
                      className={`block ${index === 0 ? 'lg:col-span-2' : ''}`}
                    >
                      <Card
                        className="group relative overflow-hidden bg-white border border-gray-200 hover:border-[#7A5AFF]/50 transform hover:scale-105 transition-all duration-500 hover:shadow-2xl cursor-pointer h-full"
                      >
                        {/* Animated Background Gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${feature.color || 'from-[#7A5AFF] to-[#5a3fd9]'} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                        <CardContent className="p-6 space-y-4 h-full flex flex-col">
                          {/* Video Icon */}
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color || 'from-[#7A5AFF] to-[#5a3fd9]'} flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                            <IconComponent className="w-7 h-7 text-white" />
                          </div>

                          {/* Category Info */}
                          <div className="space-y-2 flex-grow">
                            <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#7A5AFF] transition-colors duration-300">
                              {feature.title}
                            </h3>
                            <p className="text-sm text-gray-600">{feature.description}</p>
                          </div>

                          {/* View Button */}
                          <div className="flex items-center justify-between text-[#7A5AFF] group-hover:text-[#5a3fd9] font-medium text-sm">
                            <span>Detaylı İncele</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Sağ - İçerik (7 kolon) */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center space-x-2 bg-[#7A5AFF]/15 text-[#7A5AFF] px-6 py-3 rounded-full text-sm font-medium">
                <GraduationCap className="w-5 h-5" />
                <span>{pageContent?.additional_sections?.education?.badge_text || 'Dijital Eğitim Platformu'}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {pageContent?.additional_sections?.education?.title || 'Dijital Becerilerinizi Geliştirin'}
              </h2>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                {pageContent?.additional_sections?.education?.description || 'Uzman eğitmenlerimizden e-ticaret, dijital pazarlama, web tasarım ve yapay zeka konularında ücretsiz ve ücretli eğitimler alın. Video dersler, makaleler ve uygulamalı içeriklerle öğrenin.'}
              </p>

              {/* Özellik Listesi - veritabanından list_items */}
              <div className="space-y-3 pt-2">
                {(pageContent?.additional_sections?.education?.list_items && pageContent.additional_sections.education.list_items.length > 0
                  ? pageContent.additional_sections.education.list_items
                  : ['Video Dersler', 'Uygulamalı İçerikler', 'Sertifika Programları', '7/24 Erişim']
                ).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{typeof item === 'string' ? item : (item?.text || item)}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <a href={pageContent?.additional_sections?.education?.button_link || '/egitim'}>
                  <Button size="lg" className="bg-gradient-to-r from-[#7A5AFF] to-[#5a3fd9] hover:from-[#8b6bff] hover:to-[#6b4de6] text-white transform hover:scale-105 transition-all duration-300 text-lg px-10 py-6 shadow-lg shadow-[#7A5AFF]/30">
                    <GraduationCap className="w-5 h-5 mr-2" />
                    {pageContent?.additional_sections?.education?.button_text || 'Tüm Eğitimleri Keşfet'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section Preview */}
      <section className="py-10 md:py-20">
        <div className="container mx-auto px-6">
          {/* Facebook Post Embed - Anasayfa Blog üstü */}
          {/* 
          <div className="mb-12 flex justify-center">
            <div className="w-full max-w-xl">
              <iframe
                src="https://www.facebook.com/plugins/post.php?href=https%3A%2F%2Fwww.facebook.com%2Fpermalink.php%3Fstory_fbid%3Dpfbid06FqEY8eSX1CRUEMPUuDhcqHW3BRA45ZQMdXkiQNbzCwQU5DsCC96EeuEehr415j5l%26id%3D61584750795800&show_text=true&width=500"
                width="500"
                height="667"
                style={{ border: 'none', overflow: 'hidden', maxWidth: '100%' }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                title="Facebook Post"
              ></iframe>
            </div>
          </div>
          */}
          <div className="mb-8">
            <BlogSystem sectionContent={pageContent?.additional_sections?.blog} />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <FAQSection sectionContent={pageContent?.additional_sections?.faq} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 md:py-20 bg-gradient-to-r from-black via-[#1a0a2e] to-[#7A5AFF] text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Dijital Dönüşümünüzü Başlatın
          </h2>
          <p className="text-xl text-white/85 mb-12 max-w-3xl mx-auto">
            Uzman ekibimiz ve güvenilir çözümlerimizle dijital dünyada yerinizi alın.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button
              onClick={() => setShowConsultationForm(true)}
              size="lg"
              className="bg-[#7A5AFF] hover:bg-[#8b6bff] text-white font-bold px-8 py-4 text-xl h-[56px] shadow-lg shadow-[#7A5AFF]/40"
            >
              <Lightbulb className="w-6 h-6 mr-3" />
              Ücretli Danışmanlık Al
            </Button>

            <a
              href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent('Merhaba, dijital dönüşüm hizmetleriniz hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg h-[56px]"
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              WhatsApp İletişim
            </a>

            <a
              href="/iletisim"
              className="inline-flex items-center px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur text-white rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white/30 shadow-lg h-[56px]"
            >
              <Phone className="w-6 h-6 mr-3" />
              İletişim
            </a>
          </div>
        </div>
      </section>


      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <ConsultationForm onClose={() => setShowConsultationForm(false)} />
      )}

      {/* Project Share Form Modal */}
      {showProjectShareForm && (
        <ProjectShareForm onClose={() => setShowProjectShareForm(false)} />
      )}
    </div>
  );
};

export default EnhancedHomePage;